import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import InternalPageLayout from '../components/InternalPageLayout';
import { SectionHeading } from '../components/ContentComponents';
import { Award, Calendar, ArrowRight, ArrowLeft } from 'lucide-react';
import HTMLContent from '../components/HTMLContent';
import { Skeleton } from '@/components/ui/skeleton';
import { getFileUrl } from '../utils/assetUrlHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// List View Component
export function SuccessStoriesListPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/farmers/success-stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExcerpt = (html, maxLength = 150) => {
    const text = html.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <InternalPageLayout
      title="Success Stories"
      subtitle="Inspiring stories of farmers benefiting from CISH technologies"
      breadcrumbs={[
        { label: 'For Farmers', href: '/farmers' },
        { label: 'Success Stories' }
      ]}
    >
      {loading ? (
        <div className="grid md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-48 rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : stories.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {stories.map(story => (
            <Link key={story.id} to={`/success-stories/${story.slug}`}>
              <div className="group bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-shadow h-full">
                {story.image && (
                  <img
                    src={getFileUrl(story.image)}
                    alt={story.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#0E6D2B] transition-colors">
                    {story.title}
                  </h3>
                  {story.date && (
                    <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {story.date}
                    </p>
                  )}
                  <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                    {getExcerpt(story.overview)}
                  </p>
                  <span className="text-[#0E6D2B] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No success stories available</p>
        </div>
      )}
    </InternalPageLayout>
  );
}

// Detail View Component
export function SuccessStoryDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStory();
  }, [slug]);

  const fetchStory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/farmers/success-stories/${slug}`);
      setStory(response.data);
    } catch (error) {
      console.error('Error fetching story:', error);
      if (error.response?.status === 404) {
        navigate('/success-stories');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <InternalPageLayout
        title="Loading..."
        breadcrumbs={[
          { label: 'For Farmers', href: '/farmers' },
          { label: 'Success Stories', href: '/success-stories' },
          { label: 'Loading...' }
        ]}
      >
        <Skeleton className="h-96 rounded-lg" />
      </InternalPageLayout>
    );
  }

  if (!story) return null;

  return (
    <InternalPageLayout
      title={story.title}
      breadcrumbs={[
        { label: 'For Farmers', href: '/farmers' },
        { label: 'Success Stories', href: '/success-stories' },
        { label: story.title }
      ]}
    >
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/success-stories')}
          className="flex items-center gap-2 text-[#0E6D2B] hover:text-[#0a5a23] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Success Stories
        </button>

        {story.image && (
          <img
            src={getFileUrl(story.image)}
            alt={story.title}
            className="w-full h-64 md:h-96 object-cover rounded-xl mb-8 shadow-lg"
          />
        )}

        {story.date && (
          <p className="text-sm text-gray-500 mb-6 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {story.date}
          </p>
        )}

        {/* Overview */}
        {story.overview && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Overview</h2>
            <div className="prose max-w-none">
              <HTMLContent html={story.overview} />
            </div>
          </div>
        )}

        {/* Initiatives */}
        {story.initiatives && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Initiatives</h2>
            <div className="prose max-w-none">
              <HTMLContent html={story.initiatives} />
            </div>
          </div>
        )}

        {/* Outcome */}
        {story.outcome && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Outcome</h2>
            <div className="prose max-w-none">
              <HTMLContent html={story.outcome} />
            </div>
          </div>
        )}

        {/* Impact */}
        {story.impact && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Impact</h2>
            <div className="prose max-w-none bg-green-50 border-l-4 border-[#0E6D2B] p-6 rounded-r-lg">
              <HTMLContent html={story.impact} />
            </div>
          </div>
        )}
      </div>
    </InternalPageLayout>
  );
}

// Default export for list view
export default SuccessStoriesListPage;
